import { useEffect, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { scenarioService, type MyScenario } from '../../services/scenario';
import {
  buildHomeScenarioCardPool,
  formatScenarioLastUsedAt,
} from '../../services/recentScenarioModel';
import { i18nService } from '../../services/i18n';
import ScenarioAppIcon from '../scenarios/ScenarioAppIcon';
import ScenarioDetailDialog from '../scenarios/ScenarioDetailDialog';

/** 首页卡片只负责推荐和预览；确认使用仍委托 App 的统一准备流程。 */
export default function RecentScenarioCards({
  onSelectScenario,
  onShowAll,
}: {
  onSelectScenario: (scenario: MyScenario) => void;
  onShowAll?: () => void;
}) {
  const [, refresh] = useState(0);
  const [batch, setBatch] = useState(0);
  const [preview, setPreview] = useState<MyScenario | null>(null);
  useEffect(() => {
    const unsubscribe = scenarioService.subscribe(() => {
      refresh((value) => value + 1);
      setBatch(0);
    });
    const unsubscribeLanguage = i18nService.subscribe(() => {
      void scenarioService.loadScenarios();
    });
    if (!scenarioService.getScenarios().length && !scenarioService.isLoading)
      void scenarioService.loadScenarios();
    const timer = window.setInterval(() => refresh((value) => value + 1), 60_000);
    return () => {
      unsubscribe();
      unsubscribeLanguage();
      window.clearInterval(timer);
    };
  }, []);
  const pool = buildHomeScenarioCardPool(
    scenarioService.getScenarios(),
    scenarioService.getRecentUsage()
  );
  if (!pool.length) return null;
  const count = Math.ceil(pool.length / 4);
  const cards = pool.slice((batch % count) * 4, ((batch % count) + 1) * 4);
  const recent = pool.some((card) => card.lastUsedAt !== null);
  return (
    <section
      className="mx-auto mt-7 w-full max-w-3xl text-left"
      aria-label={i18nService.t(recent ? 'scenariosRecentlyUsed' : 'scenariosRecommended')}
    >
      <div className="mb-2 flex items-center justify-between gap-2 text-xs">
        <h2 className="text-claude-textSecondary dark:text-claude-darkTextSecondary">
          {i18nService.t(recent ? 'scenariosRecentlyUsed' : 'scenariosRecommended')}
        </h2>
        <div className="flex gap-3">
          {count > 1 && (
            <button
              type="button"
              onClick={() => setBatch((value) => value + 1)}
              className="inline-flex items-center gap-1 text-claude-textSecondary hover:text-claude-accent dark:text-claude-darkTextSecondary"
            >
              <ArrowPathIcon className="h-3 w-3" />
              {i18nService.t('scenariosShuffle')}
            </button>
          )}
          {onShowAll && (
            <button type="button" onClick={onShowAll} className="text-claude-accent">
              {i18nService.t('scenariosViewAll')}
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        {cards.map(({ scenario, lastUsedAt }) => (
          <button
            key={scenario.id}
            type="button"
            onClick={() => setPreview(scenario)}
            className="group min-w-0 rounded-xl border border-claude-border bg-white p-3 text-left transition-colors duration-200 hover:border-claude-accent/50 dark:border-claude-darkBorder dark:bg-claude-darkSurface motion-reduce:transition-none"
          >
            <span className="flex min-w-0 items-center gap-2">
              <ScenarioAppIcon scenario={scenario} size={36} className="shrink-0 rounded-lg" />
              <span className="line-clamp-2 break-words text-sm font-medium">{scenario.name}</span>
            </span>
            <span className="mt-2 line-clamp-2 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
              {scenario.description || scenario.group_name}
            </span>
            <span className="mt-2 block text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
              {lastUsedAt
                ? i18nService
                    .t('scenariosUsedAt')
                    .replace(
                      '{time}',
                      formatScenarioLastUsedAt(lastUsedAt, i18nService.getLanguage())
                    )
                : i18nService.t('scenariosRecommended')}
            </span>
          </button>
        ))}
      </div>
      {preview && (
        <ScenarioDetailDialog
          key={preview.id}
          scenario={preview}
          onClose={() => setPreview(null)}
          onUse={onSelectScenario}
        />
      )}
    </section>
  );
}
